import {
  FormFieldWidget,
  FormM2MTableFieldWidget,
  ModelFieldType,
  SPI,
  ViewType,
  Widget,
  http,
  ValidatorInfo,
  ValidatorStatus,
  isValidatorSuccess
} from '@kunlun/dependencies';
import { cloneDeep } from 'lodash-es';
import XLSX from 'xlsx';
import FileSaver from 'file-saver';
import { OioMessage, OioNotification } from '@kunlun/vue-ui-antd';
import AfterSalesExchangeGood from './AfterSalesExchangeGood.vue';
import { removeDecimalZero } from '../../../../utils/number';

@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: ViewType.Form,
    ttype: ModelFieldType.ManyToMany,
    widget: 'Table',
    name: 'detailProxySaleSkus',
    model: 'deli.aries.seller.DeliAriesSellerTradeReverseOrderCreate',
    multi: true
  })
)
@SPI.ClassFactory(
  FormFieldWidget.Token({
    viewType: ViewType.Form,
    ttype: ModelFieldType.ManyToMany,
    widget: 'Table',
    name: 'detailProxySaleSkus',
    model: 'deli.aries.seller.DeliAriesSellerTradeReverseOrderEdit',
    multi: true
  })
)
export class FormM2mMultiAfterSalesExchangeGoodFieldWidget extends FormM2MTableFieldWidget {
  public initialize(props) {
    super.initialize(props);
    this.setComponent(AfterSalesExchangeGood);
    return this;
  }

  private getGqlQueryName() {
    const map = {
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderCreate': 'deliAriesSellerTradeReverseOrderCreateQuery',
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderEdit': 'deliAriesSellerTradeReverseOrderEditQuery'
    } as const;
    const name = map[this.field.model];
    if (!name) throw new Error('name not found');
    return name;
  }

  @Widget.Method()
  private getGqlMutationName() {
    const map = {
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderCreate': 'deliAriesSellerTradeReverseOrderCreateMutation',
      'deli.aries.seller.DeliAriesSellerTradeReverseOrderEdit': 'deliAriesSellerTradeReverseOrderEditMutation'
    } as const;
    const name = map[this.field.model];
    if (!name) throw new Error('name not found');
    return name;
  }

  private stringifyToGraphql(obj: any, enumPropNameList: string[] = []) {
    let json = JSON.stringify(obj);
    // 去除枚举属性值的引号
    if (enumPropNameList.length > 0) {
      const reg = new RegExp(`"(${enumPropNameList.join('|')})":"(.*?)"`, 'g');
      json = json.replace(reg, '"$1":$2');
    }
    // 去除所有属性名的引号
    const reg2 = new RegExp(`((?!\\\\).)"([^,]*?)((?!\\\\).)":`, 'g');

    return json.replace(reg2, '$1$2$3:');
  }

  private iterateObjDeep(source: Record<any, any> | any[], fn: (obj: Record<any, any>) => any) {
    if (Array.isArray(source)) {
      source.forEach((item) => {
        if (Object.prototype.toString.call(item) === '[object Object]') {
          fn(item);
          Object.values(source).forEach((item2) => {
            this.iterateObjDeep(item2, fn);
          });
        }
      });
    } else if (Object.prototype.toString.call(source) === '[object Object]') {
      fn(source);
      Object.values(source).forEach((item) => {
        this.iterateObjDeep(item, fn);
      });
    }
  }

  @Widget.Method()
  async fetchData(itemDetailStr: string) {
    if (
      !this.formData.sellerId ||
      !this.formData.buyerId ||
      !this.formData.reverseType ||
      !this.formData.salesOfficeId
    ) {
      OioMessage.error('请将买卖家填写完整!');
      return;
    }
    if (!this.formData.buyerRelationApplyProxy || !this.formData.reverseType) {
      OioMessage.error('请将买家及售后方案信息填写完整!');
      return;
    }
    if (!this.formData.reverseDisChannel) {
      OioMessage.error('请将分销渠道填写完整!');
      return;
    }

    const data: Record<any, any> = {
      detailProxySaleSkus: cloneDeep(this.formData.detailProxySaleSkus as any[]).filter((item) => item.outCode),
      itemDetailStr,
      buyerRelationApplyProxy: cloneDeep(this.formData.buyerRelationApplyProxy as any),
      reverseType: this.formData.reverseType,
      sellerId: this.formData.sellerId,
      buyerId: this.formData.buyerId,
      salesOfficeId: this.formData.salesOfficeId,
      reverseDisChannel: cloneDeep(this.formData.reverseDisChannel as any)
    };

    this.iterateObjDeep(data, (obj: Record<any, any>) => {
      delete obj.__draftId;
      delete obj._X_ROW_KEY;
    });

    const gql = `
      query {
        ${this.getGqlQueryName()} {
          addItemDetail(
            data: ${this.stringifyToGraphql(data, ['reverseType', 'priceType'])}
          ) {
            reminds
            isMerged
            errMessages
            refundAmount
            detailProxySaleSkus {
              id
              itemCode
              itemName
              itemNumber
              minMarketPrice
              outCode
              reverseUnitPrice
              saleUnitName
              applyReverseNum
              applyRefundAmount
              actualUnitPrice
              characteristics
              priceType
              externalMaterialGroup
              reverseDetailId
              applyRefundUnitPrice
              deliAriesItemSpecificationsProxyList {
                id
                pcsCount
                count
                unitName
                unitCode
                conversionRules
                isEnabled
              }
              reason {
                id
                code
                reason
              }
            }
          }
        }
      }    
    `;

    const res = await http.query(this.model.moduleName, gql);

    const resData = res.data[this.getGqlQueryName()].addItemDetail;

    if ((resData.reminds as string[])?.length) {
      OioNotification.warning('提示', (resData.reminds as string[]).join('\n'));
    }

    return resData;
  }

  @Widget.Method()
  async updateOne(row: any) {
    if (
      !this.formData.sellerId ||
      !this.formData.buyerId ||
      !this.formData.reverseType ||
      !this.formData.salesOfficeId
    ) {
      OioMessage.error('请将买卖家填写完整!');
      return;
    }
    if (!this.formData.reverseDisChannel) {
      OioMessage.error('请将分销渠道填写完整!');
      return;
    }

    const create: Record<any, any> = {
      detailProxySaleSkus: cloneDeep(this.formData.detailProxySaleSkus as any[]).filter((item) => item.outCode),
      buyerRelationApplyProxy: cloneDeep(this.formData.buyerRelationApplyProxy as any),
      reverseType: this.formData.reverseType,
      sellerId: this.formData.sellerId,
      buyerId: this.formData.buyerId,
      salesOfficeId: this.formData.salesOfficeId,
      deliAriesSalesOrganization: cloneDeep(this.formData.deliAriesSalesOrganization as any),
      reverseDisChannel: cloneDeep(this.formData.reverseDisChannel as any)
    };

    const copyRow = cloneDeep(row);
    // 不传row表示进行删除动作
    if (row) {
      if (!Object.values(copyRow).some((item) => item !== null && item !== undefined)) {
        return;
      }

      const index = (create.detailProxySaleSkus as any[]).findIndex((i) => i.outCode === copyRow.outCode);
      if (index !== -1) {
        create.detailProxySaleSkus.splice(index, 1);
      }
    }

    this.iterateObjDeep([create, copyRow], (obj: Record<any, any>) => {
      delete obj.__draftId;
      delete obj._X_ROW_KEY;
    });

    const gql = `
    query {
      ${this.getGqlQueryName()} {
        itemDetailConstructMirror(
          create: ${this.stringifyToGraphql(create, ['reverseType', 'priceType'])}
          data: ${this.stringifyToGraphql(copyRow ?? {}, ['priceType'])}
        ) {
          reminds
          errMessages
          refundAmount
          detailProxySaleSkus {
              id
              itemCode
              itemName
              itemNumber
              minMarketPrice
              outCode
              reverseUnitPrice
              saleUnitName
              applyReverseNum
              applyRefundAmount
              actualUnitPrice
              characteristics
              priceType
              externalMaterialGroup
              reverseDetailId
              applyRefundUnitPrice
              deliAriesItemSpecificationsProxyList {
                id
                pcsCount
                count
                unitName
                unitCode
                conversionRules
                isEnabled
              }
              reason {
                id
                code
                reason
              }
            }
        }
      }
    }
    `;
    const res = await http.query(this.model.moduleName, gql);

    const resData = res.data[this.getGqlQueryName()].itemDetailConstructMirror;

    if ((resData.reminds as string[])?.length) {
      OioNotification.warning('提示', (resData.reminds as string[]).join('\n'));
    }

    return resData;
  }

  @Widget.Method()
  public exportFile(dataSource: any[]) {
    const priceTypeEnumList = {
      EX_FACTORY_PRICE: '出厂价',
      PRICE: '一口价',
      REAL_PRICE: '实价',
      RETAIL_PRICE: '零售价'
    };
    const content = dataSource.map((item) => ({
      ERP商品编码: item.outCode,
      商品名称: item.itemName,
      商品编码: item.itemCode,
      货号: item.itemNumber,
      产品特性: item.characteristics,
      价格类型: priceTypeEnumList[item.priceType],
      外部物料组: item.externalMaterialGroup,
      单位: item.saleUnitName,
      出厂价: removeDecimalZero(item.minMarketPrice),
      成交单价: removeDecimalZero(item.actualUnitPrice),
      申请售后数量: item.applyReverseNum,
      售后单价: removeDecimalZero(item.reverseUnitPrice),
      申请售后金额: removeDecimalZero(item.applyRefundAmount),
      售后原因: item.reason?.reason
    }));
    this.saveJsonToExcel(content, `代客下单商品明细${Date.now()}.xlsx`);
  }

  public saveJsonToExcel(data, filename) {
    const sheet = XLSX.utils.json_to_sheet(data);
    const workbook = {
      SheetNames: ['sheet1'],
      Sheets: {
        sheet1: sheet
      }
    };
    const wbout = XLSX.write(workbook, {
      bookType: 'xlsx',
      bookSST: true,
      type: 'array'
    });
    FileSaver.saveAs(new Blob([wbout], { type: 'application/octet-stream' }), filename);
  }

  @Widget.Reactive()
  public setFormData(val) {
    this.change(val);
  }

  @Widget.Reactive()
  public _formData: Record<any, any> = {};

  @Widget.Watch('formData', { deep: true, immediate: true })
  public watchFormData(val) {
    this._formData = val;
  }

  @Widget.Reactive()
  private mountedProcessFlag = false;

  protected mounted() {
    super.mounted();
    if (this.mountedCallChaining) {
      this.mountedCallChaining.hook(this.path, async () => {
        await this.mountedProcess();
      });
    } else {
      this.mountedProcess();
    }
  }

  protected async mountedProcess() {
    this.mountedProcessFlag = true;
  }

  public async validator(): Promise<ValidatorInfo> {
    const res = await this.validatorSpecific(this.value);

    if (!isValidatorSuccess(res)) {
      return res;
    }

    this.formData.detailProxySaleSkus =
      (this.formData.detailProxySaleSkus as any[])?.filter((item) => item.outCode) ?? [];

    return {
      message: '',
      status: ValidatorStatus.Success,
      path: ''
    };
  }
}
